import '@fontsource/dm-sans';
import '@fontsource/roboto';
import {ThemeProvider} from '@mui/material/styles';
import {ReactKeycloakProvider} from '@react-keycloak/web';
import React, {useEffect, useState} from 'react';
import {withCookies} from 'react-cookie';
import {BrowserRouter, Route, Routes} from 'react-router-dom';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import './App.css';
import keycloak from './Keycloak';
import axios from './axios';
import {
  Communities,
  CommunityDetail,
  Establishment,
  FirstPage,
  Footer,
  Header,
  Login,
  Search,
} from './components';
import Advice from './components/advice/Advice';
import AdviceDetail from './components/advice/AdviceDetail';
import AdviceSameCategoryList from './components/advice/AdviceSameCategoryList';
import {AdviceListContextProvider} from './components/advice/state/adviceContext';
import {withAuthContext} from './components/auth/context/authContext';
import NotificationConfigList from './components/configuration/notification/NotificationConfigList';
import {useDarkMode} from './components/darkMode/hook/useDarkMode';
import {withDarkModeContext} from './components/darkMode/state/darkModeContext';
import Game from './components/dashboard/Game';
import Favorite from './components/favorite/Favorite';
import CanSubscribeRoute from './components/helper/CanSubscribeRoute';
import PrivateRoute from './components/helper/PrivateRoute';
import Professional from './components/home/Professional';
import OrderDetail from './components/marketPlace/OrderDetail';
import OrderHistory from './components/marketPlace/OrderHistory';
import ProductSearch from './components/marketPlace/ProductSearch';
import ItemDetail from './components/marketPlace/itemDetail';
import {withNewsFeedContext} from './components/newsFeed/state/newsFeedContext';
import NotificationList from './components/notification/NotificationList';
import PaymentReturn from './components/payment/PaymentReturn';
import {usePoliceMode} from './components/policeMode/hook/usePoliceMode';
import {withPoliceModeContext} from './components/policeMode/state/policeModeContext';
import Products from './components/product/Products';
import Profile from './components/profile/Profile';
import Registration from './components/registration/Registration';
import Service from './components/service/Service';
import ServiceDetail from './components/service/ServiceDetail';
import ServiceList from './components/service/ServiceList';
import {ServiceListContextProvider} from './components/service/state/serviceContext';
import UserSubscription from './components/subscription/UserSubscription';
import {withSubscriptionContext} from './components/subscription/state/subscriptionContext';
import SubscriptionChoice from './components/subscription/subscriptionChoice';
import SubscriptionDetail from './components/subscription/subscriptionDetail';
import SubscriptionFailed from './components/subscription/subscriptionFailed';
import useToken from './components/useToken';
import {withRoleContext} from './components/userInfo/context/roleContext';
import {withSwitchContext} from './components/userInfo/context/switchContext';
import {withUserInfoContext} from './components/userInfo/context/userInfoContext';
import {Box} from '@mui/material';
import SubscribedRoute from './components/helper/SubscribedRoute';
import FAQList from './components/faq/FAQList';
import FAQDetail from './components/faq/FAQDetail';
import {Toaster} from 'react-hot-toast';
import RegistrationSuccess from './components/template/RegistrationSuccess';
import DirectLogin from './components/template/DirectLogin';
import {withNotificationList} from './components/notification/state/notificationListContext';

const App = () => {
  const {token, setToken} = useToken();
  const [showNav, setShowNav] = useState(false);
  const [searchResponse, setSearchResponse] = useState([]);
  const [searchInput, setSearchInput] = useState([]);
  const {mode} = useDarkMode();
  const {police} = usePoliceMode();

  const [logo, setLogo] = useState({
    image: {
      alternativeText: '',
      uri: '',
    },
    url: {
      link: '',
    },
  });

  useEffect(() => {
    axios
      .get(`${axios.defaults.apiURL}/logo`)
      .then(function (response) {
        if (response.data.length > 0) {
          setLogo(response.data[0]);
        }
      })
      .catch(function (error) {
        console.log(error);
      });
  }, []);

  return (
    <ReactKeycloakProvider authClient={keycloak}>
      <BrowserRouter>
        <ThemeProvider theme={police ? police.settings : ''}>
          <Header
            token={token}
            logo={logo}
            setToken={setToken}
            setSearchResponse={setSearchResponse}
            setSearchInput={setSearchInput}
          />
          <Toaster />
          <Box
            sx={{
              backgroundColor: mode.settings.backgroundColor,
              flexGrow: 1,
              marginTop: {xs: '75px', md: '75px', lg: '95px'},
            }}>
            <Routes>
              <Route
                path="/"
                element={<FirstPage token={token} setShowNav={setShowNav} />}
              />
              <Route
                path="/registration-success"
                element={<RegistrationSuccess setShowNav={setShowNav} />}
              />
              <Route path="/direct-login" element={<DirectLogin />} />
              <Route
                path="communities"
                element={
                  <SubscribedRoute>
                    <Communities token={token} setShowNav={setShowNav} />
                  </SubscribedRoute>
                }
              />
              <Route
                path="community/:id"
                element={
                  <SubscribedRoute>
                    <PrivateRoute>
                      <CommunityDetail token={token} setShowNav={setShowNav} />
                    </PrivateRoute>
                  </SubscribedRoute>
                }
              />

              <Route
                path="establishment/:id"
                element={<Establishment token={token} />}
              />
              <Route
                path="profile"
                element={<Profile setShowNav={setShowNav} />}
              />
              <Route
                path="login"
                element={<Login setToken={setToken} setShowNav={setShowNav} />}
              />
              <Route
                path="search"
                element={
                  <SubscribedRoute>
                    <Search token={token} setShowNav={setShowNav} />
                  </SubscribedRoute>
                }></Route>
              <Route
                path="subscription-choice"
                element={<SubscriptionChoice setShowNav={setShowNav} />}
              />
              <Route
                path="user-subscription"
                element={
                  <CanSubscribeRoute>
                    <UserSubscription setShowNav={setShowNav} />
                  </CanSubscribeRoute>
                }
              />
              <Route
                path={'subscription-detail/:id'}
                element={
                  <CanSubscribeRoute>
                    <SubscriptionDetail setShowNav={setShowNav} />
                  </CanSubscribeRoute>
                }
              />
              <Route
                path={'subscription-failed'}
                element={
                  <CanSubscribeRoute>
                    <SubscriptionFailed setShowNav={setShowNav} />
                  </CanSubscribeRoute>
                }
              />
              <Route
                path={'favorite'}
                element={
                  <SubscribedRoute>
                    <Favorite setShowNav={setShowNav} />
                  </SubscribedRoute>
                }
              />
              <Route
                path={'professional'}
                element={<Professional setShowNav={setShowNav} />}
              />
              <Route
                path={'registration'}
                element={<Registration setShowNav={setShowNav} />}
              />
              <Route path={'game'} element={<Game setShowNav={setShowNav} />} />
              <Route
                path={'item-detail/:id'}
                element={<ItemDetail setShowNav={setShowNav} />}
              />
              <Route
                path={'order-history'}
                element={<OrderHistory setShowNav={setShowNav} />}></Route>
              <Route
                path={'order-detail/:id'}
                element={<OrderDetail setShowNav={setShowNav} />}></Route>
              <Route
                path={'product-search'}
                element={<ProductSearch setShowNav={setShowNav} />}
              />
            </Routes>
            <Routes>
              <Route
                path={'/payment-return'}
                element={<PaymentReturn setShowNav={setShowNav} />}
              />
            </Routes>
            <ServiceListContextProvider>
              <Routes>
                <Route
                  path="services"
                  element={
                    <SubscribedRoute>
                      <ServiceList />
                    </SubscribedRoute>
                  }
                />
                <Route
                  path="service/:variant/:id"
                  element={
                    <SubscribedRoute>
                      <ServiceDetail />
                    </SubscribedRoute>
                  }
                />
                <Route
                  path="service"
                  element={
                    <SubscribedRoute>
                      <Service setShowNav={setShowNav} />
                    </SubscribedRoute>
                  }
                />
                <Route
                  path="products"
                  element={
                    <SubscribedRoute>
                      <Products setShowNav={setShowNav} />
                    </SubscribedRoute>
                  }
                />
              </Routes>
            </ServiceListContextProvider>
            <AdviceListContextProvider>
              <Routes>
                <Route
                  path="advices"
                  element={
                    <SubscribedRoute>
                      <Advice />
                    </SubscribedRoute>
                  }
                />

                <Route
                  path="advice/:id/:categoryId"
                  element={
                    <SubscribedRoute>
                      <AdviceSameCategoryList />
                    </SubscribedRoute>
                  }
                />
              </Routes>
            </AdviceListContextProvider>
            <Routes>
              <Route
                path="advice/:id"
                element={
                  <SubscribedRoute>
                    <AdviceDetail />
                  </SubscribedRoute>
                }
              />
            </Routes>
            <Routes>
              <Route
                path="configurations/notification"
                element={<NotificationConfigList setShowNav={setShowNav} />}
              />
              <Route
                path="notifications"
                element={<NotificationList setShowNav={setShowNav} />}
              />
              <Route path="/faq" element={<FAQList />} />
              <Route path="/faq/:id" element={<FAQDetail />} />
            </Routes>
          </Box>
          {showNav ? <Footer logo={logo} /> : ''}
        </ThemeProvider>
      </BrowserRouter>
    </ReactKeycloakProvider>
  );
};

export default withSubscriptionContext(
  withCookies(
    withNewsFeedContext(
      withDarkModeContext(
        withRoleContext(
          withAuthContext(
            withSwitchContext(
              withUserInfoContext(
                withPoliceModeContext(withNotificationList(App)),
              ),
            ),
          ),
        ),
      ),
    ),
  ),
);
